import React, { useState, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, ArrowLeft, ArrowRight } from 'lucide-react';
import biliard from '../assets/project/billiard.png';
import biliard2 from '../assets/project/billiard2.png';
import depot from '../assets/project/depot.jpg';
import depot2 from '../assets/project/depot 2.jpg';
import depot3 from '../assets/project/depot 3.jpg';
import djaya1 from '../assets/project/djaya1.png';
import djaya2 from '../assets/project/djaya2.png';
import kallaTracking from '../assets/project/KallaTracking.jpg';
import kallaTracking2 from '../assets/project/KallaTracking2.jpg';
import urban1 from '../assets/project/urban1.png';
import urban2 from '../assets/project/urban2.png';
import vinResto from '../assets/project/VinRestso.jpg';
import warehouse1 from '../assets/project/warehouse1.png';


const projects = [
  {
    title: 'Billiard Management System',
    description: 'Comprehensive billiard venue management system with table tracking, cashier integration, and real-time reporting dashboard.',
    category: 'Retail System',
    images: [biliard, biliard2]
  },
  {
    title: 'Depot POS Web and App',
    description: 'Point of sale system for depot business with inventory control, sales analytics, and mobile cashier application.',
    category: 'Point of Sale',
    images: [depot, depot2, depot3]
  },
  {
    title: 'Expedition Web and Finance System',
    description: 'Logistics and expedition management platform with shipment tracking, finance module, and operational reporting.',
    category: 'Logistics',
    images: [djaya1, djaya2]
  },
  {
    title: 'Tracking App',
    description: 'Real-time vehicle and operational tracking system integrated with dashboard analytics and reporting tools.',
    category: 'Tracking System',
    images: [kallaTracking, kallaTracking2]
  },
  {
    title: 'HRIS and POS System',
    description: 'Integrated HRIS and POS solution including employee management, payroll module, and retail transaction system.',
    category: 'Enterprise System',
    images: [urban1, urban2]
  },
  {
    title: 'Restaurant ERP System',
    description: 'End-to-end restaurant ERP covering ordering, kitchen display system, inventory control, and financial reporting.',
    category: 'ERP',
    images: [vinResto]
  },
  {
    title: 'Warehouse ERP System',
    description: 'Warehouse and inventory management system with stock monitoring, movement tracking, and distribution reporting.',
    category: 'ERP',
    images: [warehouse1]
  }
]
const Portfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  const openModal = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
  };

  const closeModal = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) => (prev + 1) % selectedProject.images.length);
    }
  };

  const prevImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) => (prev - 1 + selectedProject.images.length) % selectedProject.images.length);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = window.innerWidth < 640 ? 300 : 800;
      scrollContainerRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = window.innerWidth < 640 ? 300 : 800;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section id="portfolio" className="relative py-16 sm:py-24 md:py-32 px-4 sm:px-6" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 sm:mb-16" data-aos="fade-up">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4" data-testid="portfolio-title">Selected Works</h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base px-4">
            Showcasing our latest digital solutions
          </p>
        </div>
        
        <div className="relative">
          {/* Scroll Left Button */}
          <button
            onClick={scrollLeft}
            className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 sm:p-3 rounded-full bg-[#00E5FF]/20 backdrop-blur-sm hover:bg-[#00E5FF]/30 transition-all border border-[#00E5FF]/50 -ml-4"
            data-testid="scroll-left-button"
            aria-label="Scroll left"
          >
            <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 text-[#00E5FF]" />
          </button>

          {/* Scroll Right Button */}
          <button
            onClick={scrollRight}
            className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 sm:p-3 rounded-full bg-[#00E5FF]/20 backdrop-blur-sm hover:bg-[#00E5FF]/30 transition-all border border-[#00E5FF]/50 -mr-4"
            data-testid="scroll-right-button"
            aria-label="Scroll right"
          >
            <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 text-[#00E5FF]" />
          </button>

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="overflow-x-auto overflow-y-hidden scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="grid grid-rows-2 grid-flow-col auto-cols-[minmax(280px,1fr)] sm:auto-cols-[minmax(320px,1fr)] md:auto-cols-[minmax(350px,1fr)] gap-4 sm:gap-6 pb-4">
              {projects.map((project, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-2xl cursor-pointer h-60 sm:h-72 md:h-80"
                  data-aos="fade-up"
                  data-aos-delay={index * 50}
                  onClick={() => openModal(project)}
                  data-testid={`portfolio-card-${index}`}
                >
                  <img
                    src={project.images[0]}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F1A] via-[#0B0F1A]/80 to-transparent opacity-90 group-hover:opacity-95 transition-opacity"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                    <div className="inline-block px-2 sm:px-3 py-1 rounded-full bg-[#00E5FF]/20 text-[#00E5FF] text-xs font-semibold mb-2 sm:mb-3">
                      {project.category}
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2" data-testid={`portfolio-title-${index}`}>
                      {project.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-400 line-clamp-2">{project.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Scroll Buttons */}
        <div className="flex sm:hidden justify-center gap-4 mt-6">
          <button
            onClick={scrollLeft}
            className="p-3 rounded-full bg-[#00E5FF]/20 backdrop-blur-sm hover:bg-[#00E5FF]/30 transition-all border border-[#00E5FF]/50"
            aria-label="Scroll left"
          >
            <ArrowLeft className="w-5 h-5 text-[#00E5FF]" />
          </button>
          <button
            onClick={scrollRight}
            className="p-3 rounded-full bg-[#00E5FF]/20 backdrop-blur-sm hover:bg-[#00E5FF]/30 transition-all border border-[#00E5FF]/50"
            aria-label="Scroll right"
          >
            <ArrowRight className="w-5 h-5 text-[#00E5FF]" />
          </button>
        </div>

        {/* Scroll Indicator */}
        <div className="flex justify-center mt-6 sm:mt-8 gap-2">
          {Array.from({ length: Math.ceil(projects.length / 6) }).map((_, index) => (
            <div
              key={index}
              className="w-2 h-2 rounded-full bg-[#00E5FF]/30 transition-all"
            />
          ))}
        </div>
      </div>

      {/* Modal */}
      {selectedProject && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={closeModal}
          data-testid="portfolio-modal"
        >
          <div
            className="relative max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute -top-10 sm:-top-12 right-0 text-white hover:text-[#00E5FF] transition-colors"
              data-testid="modal-close-button"
            >
              <X className="w-6 h-6 sm:w-8 sm:h-8" />
            </button>

            <div className="relative">
              <img
                src={selectedProject.images[currentImageIndex]}
                alt={selectedProject.title}
                className="w-full h-auto rounded-lg"
                data-testid="modal-image"
              />

              {selectedProject.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors"
                    data-testid="modal-prev-button"
                  >
                    <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors"
                    data-testid="modal-next-button"
                  >
                    <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>

                  <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                    {selectedProject.images.map((_, idx) => (
                      <div
                        key={idx}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          idx === currentImageIndex ? 'bg-[#00E5FF]' : 'bg-white/30'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="mt-4 sm:mt-6 text-center">
              <h3 className="text-xl sm:text-2xl font-bold mb-2">{selectedProject.title}</h3>
              <p className="text-sm sm:text-base text-gray-400">{selectedProject.description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Portfolio;
